//
// PaymentFeeMatrix.ts
//
// The pricing matrix behind the fee editors: geography × card-type rows and
// In-Person / Tap to Pay / Online channel columns, mirroring the card
// payment pricing sheet. Each non-empty cell is one underlying rule row
// (PaymentFeeTemplateLine or BusinessPaymentFee) with channel set and the
// legacy 3DS dimension left at ANY - the pricing expresses the old 3DS
// distinction through the online channel. Rules that do not fit a standard
// cell (exact countries, other brands, legacy 3DS rows) stay editable in
// the ADVANCED section.
//

enum PaymentFeeChannel
{
    Any      = 0,
    InPerson = 1,
    TapToPay = 2,
    Online   = 3
}

enum PaymentFeeThreeDSMode
{
    Any      = 0,
    No3DS    = 1,
    Only3DS  = 2
}

// The three editable channel columns, in display order.
const PaymentFeeMatrixChannels = [ PaymentFeeChannel.InPerson, PaymentFeeChannel.TapToPay, PaymentFeeChannel.Online ];

// A rule row of either entity: the two share every dimension field.
interface PaymentFeeLineLike
{
    paymentEntityType:number;
    cardBrandType:number;
    cardCountry:string;
    cardType:number;
    fundingSource:number;
    channel:number;
    threeDSMode:number;
    has3DS:boolean;
    feeFormat:string;
    transactionFeeFormat:string;
}

class PaymentFeeMatrixRowDef
{
    key:string;
    titleKey:string;
    titleDefault:string;
    cardBrandType:number;
    cardCountry:string;   // null = any country ( the outside-EEA bucket when ES + EU rows exist )
    fundingSource:number;
    cardType:number;

    constructor(key:string, titleKey:string, titleDefault:string, cardBrandType:number, cardCountry:string, fundingSource:number, cardType:number){
        this.key = key;
        this.titleKey = titleKey;
        this.titleDefault = titleDefault;
        this.cardBrandType = cardBrandType;
        this.cardCountry = cardCountry;
        this.fundingSource = fundingSource;
        this.cardType = cardType;
    }
}

class PaymentFeeMatrixSectionDef
{
    titleKey:string;
    titleDefault:string;
    rows:PaymentFeeMatrixRowDef[];

    constructor(titleKey:string, titleDefault:string, rows:PaymentFeeMatrixRowDef[]){
        this.titleKey = titleKey;
        this.titleDefault = titleDefault;
        this.rows = rows;
    }
}

// The standard pricing rows. "EU" covers the whole EEA server-side.
const PaymentFeeMatrixSections:PaymentFeeMatrixSectionDef[] = [
    new PaymentFeeMatrixSectionDef("CARDS_ISSUED_IN_SPAIN", "CARDS ISSUED IN SPAIN", [
        new PaymentFeeMatrixRowDef("es_debit",    "CONSUMER_DEBIT",  "CONSUMER DEBIT",  0, "ES", 1, 1),
        new PaymentFeeMatrixRowDef("es_credit",   "CONSUMER_CREDIT", "CONSUMER CREDIT", 0, "ES", 2, 1),
        new PaymentFeeMatrixRowDef("es_business", "BUSINESS_CARDS",  "BUSINESS CARDS",  0, "ES", 0, 2)
    ]),
    new PaymentFeeMatrixSectionDef("OTHER_EEA_CARDS", "OTHER EEA CARDS", [
        new PaymentFeeMatrixRowDef("eea_debit",    "CONSUMER_DEBIT",  "CONSUMER DEBIT",  0, "EU", 1, 1),
        new PaymentFeeMatrixRowDef("eea_credit",   "CONSUMER_CREDIT", "CONSUMER CREDIT", 0, "EU", 2, 1),
        new PaymentFeeMatrixRowDef("eea_business", "BUSINESS_CARDS",  "BUSINESS CARDS",  0, "EU", 0, 2)
    ]),
    new PaymentFeeMatrixSectionDef("CARDS_ISSUED_OUTSIDE_THE_EEA", "CARDS ISSUED OUTSIDE THE EEA", [
        new PaymentFeeMatrixRowDef("row_debit",    "CONSUMER_DEBIT",  "CONSUMER DEBIT",  0, null, 1, 1),
        new PaymentFeeMatrixRowDef("row_credit",   "CONSUMER_CREDIT", "CONSUMER CREDIT", 0, null, 2, 1),
        new PaymentFeeMatrixRowDef("row_business", "BUSINESS_CARDS",  "BUSINESS CARDS",  0, null, 0, 2)
    ]),
    new PaymentFeeMatrixSectionDef("ALL_GEOGRAPHIES", "ALL", [
        new PaymentFeeMatrixRowDef("amex", "AMEX", "AMEX", 1, null, 0, 0)
    ])
];

function PaymentFeeMatrixRowDefAtIndex(index:number):PaymentFeeMatrixRowDef {
    let i = index;
    for (let section of PaymentFeeMatrixSections){
        if (i < section.rows.length) return section.rows[i];
        i -= section.rows.length;
    }
    return null;
}

function PaymentFeeMatrixNormalizedCountry(value:string):string {
    if (value == null) return null;
    let v = value.trim().toUpperCase();
    if (v.length == 0) return null;
    if (v == "EEA") return "EU"; // same bucket server-side
    return v;
}

// The matrix cell key of a rule row, or null when the row belongs in the
// ADVANCED section instead.
function PaymentFeeMatrixCellKeyForLine(line:PaymentFeeLineLike):string {
    if (line.paymentEntityType != PaymentEntityType.Adyen) return null;
    if (PaymentFeeMatrixChannels.indexOf(line.channel) < 0) return null;
    if ((line.threeDSMode ?? PaymentFeeThreeDSMode.Any) != PaymentFeeThreeDSMode.Any) return null;

    let country = PaymentFeeMatrixNormalizedCountry(line.cardCountry);
    for (let section of PaymentFeeMatrixSections){
        for (let def of section.rows){
            if (def.cardBrandType != (line.cardBrandType ?? 0)) continue;
            if (PaymentFeeMatrixNormalizedCountry(def.cardCountry) != country) continue;
            if (def.fundingSource != (line.fundingSource ?? 0)) continue;
            if (def.cardType != (line.cardType ?? 0)) continue;
            return def.key + "/" + line.channel;
        }
    }
    return null;
}

function PaymentFeeMatrixCellKey(def:PaymentFeeMatrixRowDef, channel:PaymentFeeChannel):string {
    return def.key + "/" + channel;
}

// Writes one matrix row def + channel into a rule row.
function PaymentFeeMatrixApplyDims(line:PaymentFeeLineLike, def:PaymentFeeMatrixRowDef, channel:PaymentFeeChannel){
    line.paymentEntityType = PaymentEntityType.Adyen;
    line.cardBrandType = def.cardBrandType;
    line.cardCountry = def.cardCountry;
    line.fundingSource = def.fundingSource;
    line.cardType = def.cardType;
    line.channel = channel;
    line.threeDSMode = PaymentFeeThreeDSMode.Any;
    line.has3DS = false;
}

// Fee format helpers: the fixed column is a plain EUR amount
// ( transactionFeeFormat "0.16" ), the rate column a percentage
// ( feeFormat "1.19%" ). Display strips the % sign; save restores it.
function PaymentFeeMatrixRateDisplay(feeFormat:string):string {
    if (feeFormat == null) return "";
    let v = feeFormat.trim();
    if (v == "0" || v.length == 0) return "";
    return v.endsWith("%") ? v.substring(0, v.length - 1).trim() : v;
}

function PaymentFeeMatrixRateFormat(display:string):string {
    let v = (display ?? "").trim().replace(",", ".");
    if (v.endsWith("%")) v = v.substring(0, v.length - 1).trim();
    if (v.length == 0) return null;
    return v + "%";
}

function PaymentFeeMatrixFixedDisplay(transactionFeeFormat:string):string {
    if (transactionFeeFormat == null) return "";
    let v = transactionFeeFormat.trim();
    if (v == "0" || v.length == 0) return "";
    return v;
}

function PaymentFeeMatrixFixedFormat(display:string):string {
    let v = (display ?? "").trim().replace(",", ".");
    if (v.length == 0) return null;
    return v;
}
